import angular, { IPromise } from 'angular'
import map from 'lodash/map'
import VendNumber, { add, round } from 'vend-number'

import { ApiTax } from '@vend/types'
import { SaleDiscount, createSaleDiscount } from '../models/sale-discount'
import {
  calculateLspCashDiscountAmount,
  shouldApplyLspCashDiscount,
} from '../functions/payments'
import { isReturn } from '../functions/sale'
import { LineItem, LineItemTaxComponent } from '../models/line-item'
import { Sale } from '../models/sale'
import { $log, $q } from '../ng-compat/imports'
import { registerManager, taxManager } from '../ng-compat/managers'
import {
  BasicPriceInfo,
  calculateLineItemDisplayPricing,
} from '../sale-calculation/calculate-line-item'
import { calculateTaxInclusivePriceComponents } from '../sale-calculation/calculate-price'
import {
  calculateSaleCashDiscountingAdjustments,
  calculateSaleDiscount,
  calculateSaleDiscountTotals,
  calculateSaleDisplayStatus,
  calculateSaleFinals,
  calculateSaleLineItemTotals,
  calculateSalePriceAndTaxTotals,
  getSurchargeRules,
  totalSurchargeFromPayments,
} from '../sale-calculation/calculate-sale'
import {
  calculateTotalTaxRate,
  collectUniqueTaxRates,
} from '../sale-calculation/calculate-tax'
import {
  selectPreAutoTaxFinals,
  updateSaleCalculableFields,
} from '../state/slices/currentSale/currentSaleSlice'
import { store } from '../state/store'
import { getPriceBookManager } from './price-book-manager'
import { PriceLineItemOptions, unitPriceManager } from './unit-price-manager'

/**
 * Provides methods for determining the price to use for line items etc.
 */
export class PriceManager {
  /**
   * The no tax group.
   */
  private noTaxGroup: ApiTax | null = null
  private whenInitialised?: IPromise<void>

  private get priceBookManager() {
    return getPriceBookManager()
  }

  /**
   * Initialise the price manager ensuring it is ready for use (primarily once the default tax has been
   * resolved).
   *
   * @return {Promise} A promise resolved once the price manager is good to go
   */
  public init() {
    if (!this.whenInitialised) {
      this.whenInitialised = $q
        .all([
          taxManager.init(),
          this.priceBookManager.init(),
          unitPriceManager.init(),
        ])
        .then(() => this.determineNoTaxGroup())
    }

    return this.whenInitialised
  }

  /**
   * Determine the "no tax" group.
   */
  private determineNoTaxGroup(): Promise<void> {
    return taxManager.getNoTaxGroup().then(taxGroup => {
      this.noTaxGroup = taxGroup!
    })
  }

  // @todo rename this method to be more specific. It applies to finding base price off a pricebook product
  // the method name is ambiguous.
  /**
   * Calculate the display base price from a given price and tax info.
   *
   * @param  basePrice {VendNumber}
   *         The tax exclusive base price of a product
   *
   * @param  taxInfo {Tax}
   *         The tax info (rate(s)) of the product
   *
   * @return {VendNumber} The display base price for the given base price (tax inclusive or exclusive according
   *         to current outlet configuration)
   */
  private calculateDisplayBasePrice(
    basePrice: number | string,
    taxInfo: ApiTax
  ) {
    const taxInclusive = registerManager.isTaxInclusive()
    let displayBasePrice

    if (taxInclusive) {
      const taxRate = calculateTotalTaxRate(taxInfo?.rates || [])
      const taxAmount = VendNumber.round(
        VendNumber.multiply(basePrice, taxRate),
        5
      )
      displayBasePrice = VendNumber.add(basePrice, taxAmount)
    } else {
      displayBasePrice = basePrice
    }

    return VendNumber.round(displayBasePrice, 2)
  }

  /**
   * Get the base price (suitable for display) of a product identified by the given ID.
   *
   * @param  productId
   *         The ID of the product to find the base price for
   *
   * @return A promise resolved with the base price suitable for display or undefined
   *         if one could not be determined
   */
  public getBasePriceForProduct(productId: string): IPromise<string | null> {
    if (!productId) {
      $log.debug(
        '[Price Manager] Missing product id when attempting to get base price for product'
      )
    }

    return $q
      .all({
        basePriceBookProduct: this.priceBookManager.getBasePriceBookProduct(
          productId
        ),
        taxInfo: taxManager.getTaxForProduct(productId),
      })
      .then(({ basePriceBookProduct, taxInfo }) => {
        if (basePriceBookProduct) {
          return this.calculateDisplayBasePrice(
            basePriceBookProduct.price,
            taxInfo.tax!
          )
        } else {
          return null
        }
      })
  }

  /**
   * Determine the best pricing for the given line item in the context of the given sale without taking promotions
   * into account. Used to send a representation of the sale to the /api/2.0/discount API to evaluate promotions.
   *
   * @param  lineItem
   *         The line item to get the pricing for
   *
   * @param  sale
   *         The sale the line item belongs to
   *
   * @return A promise resolved with the line item pricing
   */
  public async getLineItemPricingWithoutPromotions(
    lineItem: LineItem,
    sale: Readonly<Sale>
  ): Promise<BasicPriceInfo> {
    if (this._shouldSkipPricingLineItem(lineItem)) {
      return {
        price: lineItem.price,
        tax: lineItem.tax,
        loyaltyValue: lineItem.loyaltyValue,
        explicitLoyaltyValue: lineItem.explicitLoyaltyValue,
      }
    }

    const { taxInfo } = await unitPriceManager.getTaxInfoForLineItem(
      lineItem,
      sale.customer?.id
    )

    const pricing = await unitPriceManager.getLineItemPricing(
      sale,
      lineItem,
      taxInfo,
      {
        removePromotion: true,
      }
    )

    return {
      price: pricing.price,
      tax: pricing.tax,
      loyaltyValue: pricing.loyaltyValue,
      explicitLoyaltyValue: pricing.explicitLoyaltyValue,
    }
  }

  /**
   * Update the price and tax information of the given line item in the context of the given sale. Also updates all
   * corresponding line items' display values (price and discount).
   *
   * @param  lineItem
   *         The line item to update pricing and tax info of
   *
   * @param  sale
   *         The sale the line item belongs to
   *
   * @param  [options]
   *
   * @param  [options.forceRefresh]
   *         If set to true will force the refresh of certain info (e.g. tax rates) even if it is
   *         already set
   *
   * @param [options.isUpdatingCustomer]
   *        Set to true if this recalculation of the sale is due to updating the customer. Has implications on how
   *        we treat taxes if customer taxes are involved.
   *
   * @param [options.usePricebookBestPriceAsBase]
   *        When set, overrides the `register_baseprice_as_bestprice` feature flag. When enabled, a cheaper winning
   *        non-base pricebook is treated as the line reference base (suppresses discount display vs retail BASE).
   *
   * @return A promise resolved once pricing and tax info is updated
   */
  public async updateLineItemPricesAndTaxes(
    lineItem: LineItem,
    sale: Readonly<Sale>,
    options?: Readonly<PriceLineItemOptions>
  ): Promise<LineItem> {
    if (this._shouldSkipPricingLineItem(lineItem)) {
      // Still update display prices for skipped line items based on the fixed pricing
      const displayPricing = calculateLineItemDisplayPricing(
        lineItem.basePrice,
        lineItem.baseTax,
        lineItem.price,
        lineItem.tax,
        lineItem.basePrice,
        lineItem.baseTax,
        registerManager.isTaxInclusive()
      )

      return {
        ...lineItem,

        // Display pricing
        displayBasePrice: displayPricing.displayBasePrice,
        displayPrice: displayPricing.displayPrice,
        displayDiscount: displayPricing.displayDiscount,
        displayDiscountPercent: displayPricing.displayDiscountPercent,
        displayMarkupPercent: displayPricing.displayMarkupPercent,
        displayBestDiscountPercent: displayPricing.displayBestDiscountPercent,
        displayBestDiscountPrice: displayPricing.displayBestDiscountPrice,
      }
    }

    const {
      taxInfo,
      customerTaxApplied,
    } = await unitPriceManager.getTaxInfoForLineItem(
      lineItem,
      sale.customer?.id,
      options
    )

    const lineItemPricing = await unitPriceManager.getLineItemPricing(
      sale,
      lineItem,
      taxInfo,
      options
    )

    const displayPricing = calculateLineItemDisplayPricing(
      lineItemPricing.basePrice,
      lineItemPricing.baseTax,
      lineItemPricing.price,
      lineItemPricing.tax,
      lineItemPricing.bestPrice,
      lineItemPricing.bestTax,
      registerManager.isTaxInclusive()
    )

    const { price, tax, loyaltyValue, explicitLoyaltyValue } = lineItemPricing
    const { lineItemPromotion, promotionDetails } = lineItemPricing

    return {
      ...lineItem,

      // Pricing
      basePrice: lineItemPricing.basePrice.toString(),
      baseTax: lineItemPricing.baseTax.toString(),
      price,
      tax,
      loyaltyValue,
      explicitLoyaltyValue,
      taxInfo,
      customerTaxApplied,
      lineItemPromotion,
      promotionDetails,

      // Display pricing
      displayBasePrice: displayPricing.displayBasePrice,
      displayPrice: displayPricing.displayPrice,
      displayDiscount: displayPricing.displayDiscount,
      displayDiscountPercent: displayPricing.displayDiscountPercent,
      displayMarkupPercent: displayPricing.displayMarkupPercent,
      displayBestDiscountPercent: displayPricing.displayBestDiscountPercent,
      displayBestDiscountPrice: displayPricing.displayBestDiscountPrice,
    }
  }

  /**
   * Determine whether we should skip updating/determining the pricing of a given line item and instead maintain
   * the existing prices on it (e.g. because it is confirmed or a return line item).
   *
   * Only if the line item has tax info already set do we skip updating pricing on the line item. This is so we don't
   * unintentionally proceed without setting the initial tax info on the line item when it is first added to the
   * sale (i.e. in the case when an item is added via the ClientApiManager).
   */
  private _shouldSkipPricingLineItem(lineItem: LineItem): boolean {
    return Boolean(
      lineItem.isReturn || (lineItem.taxInfo && lineItem.confirmed)
    )
  }

  /**
   * Recalculate a given sale, updating tax information and pricing on all line items and recalculating all
   * totals.
   *
   * @param  sale
   *         The sale to recalculate
   *
   * @param [options]
   *
   * @param [options.isUpdatingCustomer]
   *        Set to true if this recalculation of the sale is due to updating the customer. Has implications on how
   *        we treat taxes if customer taxes are involved.
   *
   * @return {Promise} A promise resolved once the sale has been fully recalculated
   */
  public recalculateSale(
    sale: Sale,
    options: {
      isUpdatingCustomer?: boolean
    } = {}
  ) {
    const updateLineItemOptions = {
      forceRefresh: true,
      isUpdatingCustomer: options.isUpdatingCustomer,
    }

    return Promise.all(
      map(sale.lineItems, lineItem =>
        this.updateLineItemPricesAndTaxes(
          lineItem,
          sale,
          updateLineItemOptions
        ).then(updatedLineItem => {
          sale.updateLineItem(updatedLineItem)
        })
      )
    ).then(() => this._totalSalePipeline(sale))
  }

  /**
   * Total the sale by totaling each line item and adding them all together. Also collects the unique tax
   * rates encountered in the sale and creates both totals (excluding sale discount) and finals (including
   * sale discount) objects.
   *
   * @param  sale
   *         The Sale to total all the line items and tax rates
   *
   * @return The updated, totaled sale
   */
  public totalSale(sale: Sale, recalculateLineItems: boolean = true): void {
    if (registerManager.isTaxInclusive() && recalculateLineItems) {
      this.recalculateLineItemPriceComponents(sale)
    }

    this._totalSalePipeline(sale)
  }

  public recalculateLineItemPriceComponents(sale: Sale): void {
    const recalculatedLineItems: LineItem[] = sale.lineItems.map(lineItem => {
      if (lineItem.taxInfo?.id !== this.noTaxGroup!.id) {
        const taxRate = calculateTotalTaxRate(lineItem.taxInfo?.rates || [])
        // re-calculate price components to get more accurate price and tax values
        // round to 5 dp in order to keep precision (necessary to ensure that an accurate discount percentage is calculated)
        const { price, tax } = calculateTaxInclusivePriceComponents(
          round(add(lineItem.price, lineItem.tax), 5),
          taxRate
        )

        return {
          ...lineItem,
          price,
          tax,
        }
      } else {
        return lineItem
      }
    })

    sale.setLineItems(recalculatedLineItems)
  }

  private _totalSalePipeline(sale: Sale) {
    // External state

    const isTaxInclusive = registerManager.isTaxInclusive()
    const hasCashDiscounting = registerManager.hasCashDiscounting()
    const cashDiscountingRate = hasCashDiscounting
      ? registerManager.getCashDiscountingRate()
      : '0' // Respecting the variable type to stop any effects downstream
    const noTaxGroup = this.noTaxGroup

    // Existing sale state

    const { lineItems, payments, adjustments, originalSurcharge } = sale
    const saleState = {
      displayDiscount: sale.displayDiscount,
      discountIsPercentage: sale.discountIsPercentage,
      returnDiscountPercentAsDecimal: sale.returnDiscountPercentage,
      saleStatus: sale.status,
      saleIsConfirmed: sale.isConfirmed(),
      saleIsReturn: isReturn(sale),
      saleIsSavedUnfulfilled: sale.isSavedUnfulfilled || false,
      userRemovedSaleServiceFee: sale.userRemovedSaleServiceFee,
      saleIsSavedNonTax: sale.isSavedNonTax || false,
    }

    // Pure calculations

    const {
      maxSaleDiscount,
      discountPercentAsDecimal,
    } = calculateSaleDiscountTotals({
      lineItems,
      isTaxInclusive,
      ...saleState,
    })

    const isAutoTaxActive = !!selectPreAutoTaxFinals(store.getState())

    const lineItemTotals = calculateSaleLineItemTotals({
      lineItems,
      maxSaleDiscount,
      discountPercentAsDecimal,
      ...saleState,
      isTaxInclusive,
      isAutoTaxActive,
    })

    const minSaleDiscount = calculateSaleDiscount(
      lineItemTotals.map(l => l.totals)
    )

    // Calculate sale totals
    const salePriceAndTaxTotals = calculateSalePriceAndTaxTotals(
      lineItemTotals.map(l => l.totals)
    )
    const lineItemsWithTaxInfo: ApiTax[] = []
    lineItems.forEach(lineItem => {
      if (lineItem.taxInfo) {
        lineItemsWithTaxInfo.push(lineItem.taxInfo)
      }
    })

    const rates = collectUniqueTaxRates(lineItemsWithTaxInfo)
    const lspCashDiscountPercentage = sale.lspCashDiscountPercentage ?? 0
    // LSPay cash discounting takes priority over register cash discounting.
    // When an LSPay rate is configured, do not add the legacy service fee that
    // would otherwise be removed again if the customer pays in cash.
    const effectiveCashDiscountingRate =
      lspCashDiscountPercentage > 0 ? '0' : cashDiscountingRate
    const cashDiscountingAdjustments = calculateSaleCashDiscountingAdjustments({
      saleInclusivePrice: salePriceAndTaxTotals.inclusivePrice,
      discount: minSaleDiscount,
      payments,
      cashDiscountingRate: effectiveCashDiscountingRate,
      ...saleState,
    })

    // LSPay cash discount – per-sale rate (`lspCashDiscountPercentage`), calculated on
    // the pre-tax (exclusive) total and folded into the tax-inclusive total via
    // `calculateSaleFinals`.
    //
    // Forward sale: apply only after an eligible cash tender (Cash or Cash-Concealed)
    // is on the sale. Until then totals stay undiscounted so the banner/outstanding
    // balance reflect the card price.
    //
    // Return: apply as soon as the rate is set (sale-loader copies it from the
    // original sale when that sale had LSPay cash discount). No cash payment is
    // required on the return — we are crediting the discount the customer received
    // on the original sale so the refund matches what they paid.
    //
    // Eligible tenders for the forward-sale gate are Cash and Cash-Concealed only.
    // Gift card / loyalty / store credit / other "cash discounting eligible"
    // tenders do not qualify; we check PAYMENT_TYPES explicitly rather than
    // reusing `isEligibleForCashDiscount`.

    const lineItemTaxComponents: LineItemTaxComponent[][] = lineItemTotals.map(
      ltTotal => ltTotal.taxComponents
    )

    const saleFinalsArgs = {
      lineItemTaxComponents,
      discount: minSaleDiscount,
      ecomCustomCharges: sale.ecomCustomCharges,
      ...saleState,
      isTaxInclusive,
      noTaxGroup,
      adjustments,
      surcharge: {
        amount: totalSurchargeFromPayments(payments),
        rules: getSurchargeRules(payments),
        origin: originalSurcharge,
      },
    }

    // Compute finals without cash discount first. This produces the same
    // rounded values (inclusivePrice, exclusivePrice) that the payment flow
    // reads from Redux to determine the customer's cash payment amount.
    // Using these for the eligibility check avoids precision mismatches
    // between the recorded payment and the expected discounted total.
    const baseSaleTotals = {
      ...salePriceAndTaxTotals,
      ...cashDiscountingAdjustments,
      maxSaleDiscount,
      rates,
      lspCashDiscount: '0',
    }
    const baseSaleFinals = calculateSaleFinals({
      saleTotals: baseSaleTotals,
      ...saleFinalsArgs,
    })

    const lspCashDiscountApplies =
      lspCashDiscountPercentage > 0 &&
      (saleState.saleIsReturn ||
        shouldApplyLspCashDiscount({
          saleIsReturn: false,
          lspCashDiscountPercentage,
          payments,
          saleInclusivePrice: baseSaleFinals.inclusivePrice,
          exclusivePrice: baseSaleFinals.exclusivePrice,
          tax: baseSaleFinals.tax,
        }))
    const lspCashDiscount = lspCashDiscountApplies
      ? calculateLspCashDiscountAmount(
          baseSaleFinals.exclusivePrice,
          lspCashDiscountPercentage
        )
      : '0'

    const saleTotals = { ...baseSaleTotals, lspCashDiscount }
    const saleFinals = lspCashDiscountApplies
      ? calculateSaleFinals({ saleTotals, ...saleFinalsArgs })
      : baseSaleFinals

    const saleDisplayStatus = calculateSaleDisplayStatus({
      ...saleState,
      lineItemsCount: lineItems.length,
      returnLineItemsCount: lineItems.filter(lt => lt.isReturn === true).length,
    })

    // Lastly, mutate state

    let saleDiscount: SaleDiscount | null = null
    if (minSaleDiscount) {
      saleDiscount = createSaleDiscount({
        ...sale.discount,
        price: minSaleDiscount.price,
        tax: minSaleDiscount.tax,
        taxInfo: this.noTaxGroup,
      })
    }

    const lineItemsWithTotals = sale.lineItems.map((lineItem, i) => {
      return {
        ...lineItem,
        totals: lineItemTotals[i].totals,
        taxComponents: lineItemTotals[i].taxComponents,
      }
    })

    store.dispatch(
      updateSaleCalculableFields({
        lineItems: lineItemsWithTotals,
        discount: saleDiscount,
        totals: saleTotals,
        finals: saleFinals,
        displayStatus: saleDisplayStatus,
      })
    )
  }
}

let priceManager: PriceManager | undefined

export const getPriceManager = () => {
  if (!priceManager) {
    priceManager = new PriceManager()
  }

  return priceManager
}

// Test helper to avoid singleton state leaking across suites.
export const resetPriceManager = () => {
  priceManager = undefined
}

/**
 * Responsible for updating price information based on price book details.
 *
 * @module priceManager
 */
angular
  .module('webregister.managers')
  .factory('priceManager', () => getPriceManager())
